/**
 * Relative "last active" label: `Never` for empty/sentinel values, `Today` for
 * the current day, otherwise `Nd ago`. Returns the raw string unchanged when it
 * is not a parseable date.
 */
export function formatLastActive(dateStr: string): string {
    if (!dateStr || dateStr === 'Never') return 'Never';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today.getTime() - itemDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'Today';
    return `${diffDays}d ago`;
}

/**
 * Compact relative age for a recent event — `Just now`, `12m ago`, `3h ago`,
 * `2d ago`, then a plain date past a week. Finer-grained than
 * {@link formatLastActive}, which only resolves to whole days; use this where
 * minutes matter, such as a notification feed. `—` when not parseable.
 */
export function formatRelativeTime(iso: string): string {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '—';

    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
}

/**
 * Full locale timestamp for an ISO string; `—` when the value is not parseable.
 */
export function formatTimestamp(iso: string): string {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString();
}
